/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React, {useEffect, useState} from 'react';

import {Icon} from '@gravity-ui/uikit';

import {block} from '../../utils/cn';
import {User} from '../UserMenu/types';

import federationUserIcon from '../../../assets/icons/federation-user.svg';

import './UserAvatar.scss';

export type UserData = Partial<
    Pick<User, 'avatarHost' | 'avatarId' | 'avatarData' | 'avatar' | 'login' | 'isFederationUser'>
>;

export type UserAvatarSize = 'xxs' | 'xs' | 's' | 'm' | 'l';

export interface UserAvatarProps {
    user?: UserData;
    url?: string;
    size?: UserAvatarSize;
    title?: string;
    className?: string;
    internal?: boolean;
    federation?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const textSizeMap: Record<UserAvatarSize, string> = {
    xxs: 'small',
    xs: 'small',
    s: 'middle',
    m: 'middle',
    l: '50',
};
const federationIconSizeMap: Record<UserAvatarSize, number> = {
    xxs: 8,
    xs: 8,
    s: 8,
    m: 10,
    l: 10,
};

const isRetina = typeof window === 'object' && window.devicePixelRatio > 1;

// Если url содержит '/islands-<size>', заменяем эту часть на ретине на '/islands-retina-<size>',
// в противном случае возвращаем url в исходном виде.
function toRetinaAwareUrl(url: string | undefined): string | undefined {
    return isRetina ? url?.replace(/\/islands-(?!retina-)([^/]+)$/, '/islands-retina-$1') : url;
}

export function getUserAvatar(user: UserData, size: UserAvatarSize, internal?: boolean) {
    if (user.avatarData) {
        return `data:image/jpeg;base64,${user.avatarData}`;
    }

    if (internal && user.avatarHost && user.login) {
        return `${user.avatarHost}/api/v1/user/${user.login}/avatar/100.jpg`;
    }

    let avatar = user.avatar || 'https://avatars.mds.yandex.net/get-yapic/0/0-0/';

    if (user.avatarHost && user.avatarId) {
        // for passport user
        avatar = `${user.avatarHost}/get-yapic/${user.avatarId}/`;
    } else if (user.isFederationUser && user.avatar) {
        // for federation users with link to avatar picture
        return toRetinaAwareUrl(user.avatar);
    } else if (avatar.includes('/islands-')) {
        // Для пользователей в формате claims
        return toRetinaAwareUrl(user.avatar);
    }

    return `${avatar}islands${isRetina ? '-retina' : ''}-${textSizeMap[size]}`;
}

const b = block('user-avatar');

/**
 * @deprecated Use UserAvatar from '@yandex-data-ui/common'
 */
export function UserAvatar(props: UserAvatarProps) {
    const {user, url, size = 's', title, className, internal, federation, onClick} = props;
    const interactive = Boolean(onClick);

    // HACK: Used to re-render avatar image on client with real retina value (necessary for SSR)
    const [mounted, setMounted] = useState<boolean>(false);
    useEffect(() => setMounted(true), []);

    let picSrc;
    if (user) {
        picSrc = getUserAvatar(user, size, internal);
    } else if (url) {
        picSrc = url;
    }

    return (
        <div
            key={String(mounted)}
            title={title}
            className={b({size, federation, interactive}, className)}
            style={{
                backgroundImage: `url(${picSrc})`,
            }}
            onClick={onClick}
        >
            {federation && (
                <div className={b('federation')}>
                    <Icon
                        data={federationUserIcon}
                        size={federationIconSizeMap[size]}
                        className={b('federation-icon')}
                    />
                </div>
            )}
        </div>
    );
}
